/**
 * Helpers JSON-LD avancés pour le référencement IA (LLM SEO) et Google.
 *
 * Schémas générés :
 *  - HowTo (Guides "comment faire X")
 *  - Article + about (Études de cas + Comparatifs)
 *  - Product avec AggregateRating (reconditionnés)
 *  - Review individuel
 *  - VideoObject (TikTok, présentations)
 *  - Speakable (assistants vocaux, ChatGPT voice)
 *  - ItemList (collections)
 *
 * Note : Les schémas LocalBusiness/Organization globaux restent dans index.html.
 * Ces helpers ne servent QUE pour des pages spécifiques (guides, cas, comparatifs).
 */

const SITE_URL = "https://www.mediastore79.com";
const ORG_REF = `${SITE_URL}/#organization`;
const LB_REF = `${SITE_URL}/#localbusiness`;

/**
 * Construit un schéma HowTo complet pour une page guide.
 * Optimisé pour ChatGPT / Perplexity / Google rich snippet "Étapes".
 */
export function buildHowToSchema(guide) {
  const { slug, title, intro, steps, faqs, totalTimeIso = "PT15M", estimatedCost } = guide;
  const url = `${SITE_URL}/guides/${slug}`;
  const graph = [
    {
      "@type": "HowTo",
      "@id": `${url}#howto`,
      name: title,
      description: intro.slice(0, 250),
      inLanguage: "fr-FR",
      totalTime: totalTimeIso,
      url,
      image: "https://customer-assets.emergentagent.com/job_b23b68ca-96f4-4732-840a-c86194f2a639/artifacts/5g1iii6g_IMGP9226.jpg",
      ...(estimatedCost && {
        estimatedCost: {
          "@type": "MonetaryAmount",
          currency: "EUR",
          value: estimatedCost,
        },
      }),
      step: steps.map((s, idx) => ({
        "@type": "HowToStep",
        position: idx + 1,
        name: s.title,
        text: s.content,
        url: `${url}#step-${idx + 1}`,
      })),
      author: { "@id": ORG_REF },
      publisher: { "@id": ORG_REF },
      mainEntityOfPage: url,
    },
  ];
  if (faqs && faqs.length > 0) {
    graph.push({
      "@type": "FAQPage",
      "@id": `${url}#faq`,
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
  }
  return { "@context": "https://schema.org", "@graph": graph };
}

/**
 * Construit un schéma Article pour une étude de cas réelle.
 * Optimisé pour citation par LLM ("Mediastore a réparé un MacBook après...").
 */
export function buildCaseStudySchema(caseStudy) {
  const { slug, title, intro, caseDetails, faqs, publishedDate, modifiedDate } = caseStudy;
  const url = `${SITE_URL}/etudes-de-cas/${slug}`;
  const graph = [
    {
      "@type": "Article",
      "@id": `${url}#article`,
      headline: title,
      description: intro.slice(0, 250),
      inLanguage: "fr-FR",
      datePublished: publishedDate || "2026-02-10",
      dateModified: modifiedDate || "2026-02-10",
      url,
      image: "https://customer-assets.emergentagent.com/job_b23b68ca-96f4-4732-840a-c86194f2a639/artifacts/5g1iii6g_IMGP9226.jpg",
      author: { "@id": ORG_REF },
      publisher: { "@id": ORG_REF },
      mainEntityOfPage: url,
      articleSection: "Étude de cas réparation",
      about: caseDetails && {
        "@type": "Thing",
        name: caseDetails.device,
        description: `Intervention sur ${caseDetails.device} : ${caseDetails.issue}. Résolution : ${caseDetails.outcome}.`,
      },
      mentions: [{ "@id": LB_REF }],
    },
  ];
  if (faqs && faqs.length > 0) {
    graph.push({
      "@type": "FAQPage",
      "@id": `${url}#faq`,
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
  }
  // Ajout d'un Review individuel si le cas a un témoignage client
  if (caseDetails && caseDetails.clientTestimonial) {
    graph.push({
      "@type": "Review",
      "@id": `${url}#review`,
      itemReviewed: { "@id": LB_REF },
      reviewBody: caseDetails.clientTestimonial,
      author: {
        "@type": "Person",
        name: caseDetails.clientInitials || "Client Mediastore",
      },
      reviewRating: {
        "@type": "Rating",
        ratingValue: caseDetails.rating || 5,
        bestRating: 5,
        worstRating: 1,
      },
      datePublished: publishedDate || "2026-02-10",
    });
  }
  return { "@context": "https://schema.org", "@graph": graph };
}

/**
 * Construit un schéma Article pour un comparatif.
 * Mentionne les deux entités comparées pour aider les LLM à comprendre le contexte.
 */
export function buildComparisonSchema(comparison) {
  const { slug, title, intro, comparison: cmp, faqs, publishedDate, modifiedDate } = comparison;
  const url = `${SITE_URL}/comparatifs/${slug}`;
  const graph = [
    {
      "@type": "Article",
      "@id": `${url}#article`,
      headline: title,
      description: intro.slice(0, 250),
      inLanguage: "fr-FR",
      datePublished: publishedDate || "2026-02-10",
      dateModified: modifiedDate || "2026-02-10",
      url,
      image: "https://customer-assets.emergentagent.com/job_b23b68ca-96f4-4732-840a-c86194f2a639/artifacts/5g1iii6g_IMGP9226.jpg",
      author: { "@id": ORG_REF },
      publisher: { "@id": ORG_REF },
      mainEntityOfPage: url,
      articleSection: "Comparatif",
      about: cmp && [
        { "@type": "Thing", name: cmp.left.name },
        { "@type": "Thing", name: cmp.right.name },
      ],
      // Speakable : passage clé lisible par assistant vocal
      speakable: {
        "@type": "SpeakableSpecification",
        cssSelector: [".comparison-verdict", "h1", ".comparison-intro"],
      },
    },
  ];
  if (faqs && faqs.length > 0) {
    graph.push({
      "@type": "FAQPage",
      "@id": `${url}#faq`,
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
  }
  return { "@context": "https://schema.org", "@graph": graph };
}

/**
 * Construit un schéma ItemList pour les pages index (Guides, Cas, Comparatifs).
 * Aide Google à comprendre la structure de l'annuaire.
 */
export function buildItemListSchema(items, basePath, pageTitle) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: pageTitle,
    numberOfItems: items.length,
    itemListElement: items.map((item, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      url: `${SITE_URL}${basePath}/${item.slug}`,
      name: item.title,
    })),
  };
}
